import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Clock, Eye, Heart, Bookmark, Download, Linkedin, Facebook, Twitter, MessageCircle, Mail, ChevronDown, ArrowRight, Send, Play } from "lucide-react";
import { api, apiErr } from "@/lib/api";
import Header from "@/components/site/Header";
import Footer from "@/components/site/Footer";
import { NewsletterBox } from "@/pages/KnowledgeHub";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import "@/styles/hub.css";

function fmtDate(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" }); } catch { return ""; }
}

function embedUrl(url) {
  if (!url) return "";
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}`;
  return url;
}

const CTA_MAP = {
  employer: { title: "Looking to hire?", text: "Get a curated shortlist of pre-screened candidates.", label: "Request Talent", to: "/hire-talent" },
  candidate: { title: "Ready for your next role?", text: "Join our talent network and get matched with opportunities.", label: "Upload Your Resume", to: "/submit-resume" },
  hr: { title: "Join our HR community", text: "Connect with HR professionals and stay ahead of trends.", label: "Get in Touch", to: "/contact" },
  company: { title: "Book a recruitment consultation", text: "Let's plan your hiring strategy together.", label: "Book Consultation", to: "/contact" },
};

function resolveCta(post) {
  if (post.cta_type === "none") return null;
  if (post.cta_type && post.cta_type !== "auto") return CTA_MAP[post.cta_type];
  const s = post.section;
  if (["employer", "hr-learning"].includes(s)) return CTA_MAP.employer;
  if (["career"].includes(s)) return CTA_MAP.candidate;
  return CTA_MAP.employer;
}

export default function HubPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [err, setErr] = useState(false);
  const [likes, setLikes] = useState(0);
  const [liked, setLiked] = useState(false);
  const [saved, setSaved] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [authors, setAuthors] = useState([]);

  useEffect(() => {
    api.get("/content").then((r) => setContent(r.data)).catch(() => {});
    api.get("/authors").then((r) => setAuthors(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    setErr(false); setPost(null); setPlaying(false);
    api.get(`/hub/${slug}`).then((r) => {
      setPost(r.data.post); setRelated(r.data.related || []); setLikes(r.data.post.likes || 0);
      document.title = (r.data.post.seo_title || r.data.post.title) + " — Ishaan Workforce Solutions";
      const savedList = JSON.parse(localStorage.getItem("iws_bookmarks") || "[]");
      setSaved(savedList.includes(slug));
      setLiked((JSON.parse(localStorage.getItem("iws_liked") || "[]")).includes(slug));
    }).catch(() => setErr(true));
    window.scrollTo(0, 0);
  }, [slug]);

  const like = async () => {
    if (liked) return;
    try {
      const r = await api.post(`/hub/${slug}/like`);
      setLikes(r.data.likes); setLiked(true);
      const l = JSON.parse(localStorage.getItem("iws_liked") || "[]"); l.push(slug); localStorage.setItem("iws_liked", JSON.stringify(l));
    } catch { /* ignore */ }
  };
  const toggleSave = () => {
    const list = JSON.parse(localStorage.getItem("iws_bookmarks") || "[]");
    const next = saved ? list.filter((s) => s !== slug) : [...list, slug];
    localStorage.setItem("iws_bookmarks", JSON.stringify(next)); setSaved(!saved);
    toast.success(saved ? "Removed from bookmarks" : "Saved to bookmarks");
  };

  const shareUrl = typeof window !== "undefined" ? window.location.href : "";
  const shares = post ? [
    { icon: Linkedin, label: "LinkedIn", url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shareUrl)}`, t: "linkedin" },
    { icon: MessageCircle, label: "WhatsApp", url: `https://wa.me/?text=${encodeURIComponent(post.title + " " + shareUrl)}`, t: "whatsapp" },
    { icon: Facebook, label: "Facebook", url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl)}`, t: "facebook" },
    { icon: Twitter, label: "X", url: `https://twitter.com/intent/tweet?url=${encodeURIComponent(shareUrl)}&text=${encodeURIComponent(post.title)}`, t: "x" },
    { icon: Mail, label: "Email", url: `mailto:?subject=${encodeURIComponent(post.title)}&body=${encodeURIComponent(shareUrl)}`, t: "email" },
  ] : [];

  if (err) return (
    <div><Header /><section className="py-24 text-center"><p className="text-slate-600">This post is no longer available.</p><Link to="/hub" className="inline-block mt-4 text-emerald-brand font-medium">Back to Knowledge Hub</Link></section><Footer content={content} /></div>
  );
  if (!post) return <div><Header /><section className="py-24 text-center text-slate-500">Loading…</section></div>;

  const cta = resolveCta(post);
  const author = authors.find((a) => a.name === post.author);

  return (
    <div>
      <Header />
      <article data-testid="hub-post">
        <section className="bg-navy text-white py-14 relative overflow-hidden">
          <div className="absolute inset-0 grain-band opacity-30" />
          <div className="max-w-3xl mx-auto px-6 relative">
            <button onClick={() => navigate("/hub")} className="inline-flex items-center gap-1.5 text-sm text-slate-300 hover:text-white mb-4"><ArrowLeft size={16} /> Knowledge Hub</button>
            {post.category && <span className="text-xs font-medium text-emerald-light">{post.category}</span>}
            <h1 className="mt-2 font-display font-bold text-3xl lg:text-4xl tracking-tight leading-tight" data-testid="hub-post-title">{post.title}</h1>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-sm text-slate-300">
              <span>{post.author}{post.author_role ? ` · ${post.author_role}` : ""}</span>
              <span>{fmtDate(post.published_at)}</span>
              <span className="flex items-center gap-1"><Clock size={13} /> {post.reading_time || 1} min read</span>
              <span className="flex items-center gap-1"><Eye size={13} /> {post.views || 0}</span>
            </div>
          </div>
        </section>

        <section className="py-12">
          <div className="max-w-3xl mx-auto px-6">
            {post.type === "video" && post.video_url && (
              playing ? (
                <div className="aspect-video rounded-2xl overflow-hidden bg-black mb-8" data-testid="hub-video-embed">
                  <iframe src={embedUrl(post.video_url) + "?autoplay=1"} title={post.title} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                </div>
              ) : (
                <button type="button" data-testid="hub-video-play" onClick={() => { setPlaying(true); api.post(`/hub/${slug}/event`, { type: "play" }).catch(() => {}); }}
                  className="group relative aspect-video w-full rounded-2xl overflow-hidden bg-black mb-8">
                  {(post.video_thumbnail || post.cover_image) && <img src={post.video_thumbnail || post.cover_image} alt={post.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-70 transition-opacity" />}
                  <span className="absolute inset-0 flex items-center justify-center"><span className="h-16 w-16 rounded-full bg-white/90 flex items-center justify-center group-hover:scale-110 transition-transform"><Play size={26} className="text-emerald-brand ml-1" /></span></span>
                  {post.video_duration && <span className="absolute bottom-3 right-3 text-xs bg-black/70 text-white rounded px-2 py-0.5">{post.video_duration}</span>}
                </button>
              )
            )}
            {post.type !== "video" && post.cover_image && (
              <img src={post.cover_image} alt={post.title} className="w-full rounded-2xl mb-8 object-cover" data-testid="hub-cover-img" />
            )}
            {post.type === "resource" && post.resource_url && (
              <a href={post.resource_url} target="_blank" rel="noreferrer" data-testid="hub-download" onClick={() => api.post(`/hub/${slug}/event`, { type: "download" }).catch(() => {})} className="flex items-center justify-between gap-4 bg-emerald-brand/5 border border-emerald-brand/30 rounded-2xl p-6 mb-8 hover:bg-emerald-brand/10 transition-colors">
                <div className="flex items-center gap-4"><span className="h-12 w-12 rounded-xl bg-emerald-brand text-white flex items-center justify-center"><Download size={22} /></span><div><p className="font-display font-semibold text-navy">Download this resource</p><p className="text-sm text-slate-500">{post.resource_type || "File"}</p></div></div>
                <span className="text-emerald-brand font-semibold text-sm shrink-0">Download →</span>
              </a>
            )}

            {post.excerpt && <p className="text-lg text-slate-600 leading-relaxed mb-6 font-medium">{post.excerpt}</p>}
            {post.body && <div className="prose-hub" data-testid="hub-body" dangerouslySetInnerHTML={{ __html: post.body }} />}

            {(post.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-2 mt-8">
                {post.tags.map((t) => <span key={t} className="text-xs bg-slate-100 text-slate-600 rounded-full px-3 py-1">#{t}</span>)}
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-4 mt-8 py-5 border-y border-slate-200">
              <div className="flex items-center gap-2">
                <button onClick={like} data-testid="hub-like" className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${liked ? "border-red-200 bg-red-50 text-red-500" : "border-slate-200 text-slate-600 hover:border-red-200 hover:text-red-500"}`}><Heart size={16} className={liked ? "fill-red-500" : ""} /> {likes}</button>
                <button onClick={toggleSave} data-testid="hub-bookmark" className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-sm font-medium transition-colors ${saved ? "border-emerald-brand bg-emerald-brand/10 text-emerald-brand" : "border-slate-200 text-slate-600 hover:border-emerald-brand hover:text-emerald-brand"}`}><Bookmark size={16} className={saved ? "fill-emerald-brand" : ""} /> {saved ? "Saved" : "Save"}</button>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-400 mr-1">Share</span>
                {shares.map((s) => (
                  <a key={s.t} href={s.url} target="_blank" rel="noreferrer" title={s.label} data-testid={`hub-share-${s.t}`} className="h-9 w-9 inline-flex items-center justify-center rounded-full bg-slate-100 text-slate-600 hover:bg-navy hover:text-white transition-colors"><s.icon size={15} /></a>
                ))}
              </div>
            </div>

            {(post.faqs || []).length > 0 && (
              <div className="mt-10" data-testid="hub-faqs">
                <h2 className="font-display font-semibold text-navy text-2xl mb-4">Frequently Asked Questions</h2>
                <div className="space-y-2">
                  {post.faqs.map((f, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl overflow-hidden">
                      <button onClick={() => setOpenFaq(openFaq === i ? null : i)} data-testid={`hub-faq-q-${i}`} className="w-full flex items-center justify-between gap-3 px-5 py-4 text-left font-medium text-navy hover:bg-slate-50">
                        {f.q}<ChevronDown size={18} className={`shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} />
                      </button>
                      {openFaq === i && <div className="px-5 pb-4 text-sm text-slate-600 leading-relaxed whitespace-pre-line">{f.a}</div>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {cta && (
              <div className="mt-10 bg-navy text-white rounded-2xl p-8 relative overflow-hidden" data-testid="hub-cta">
                <div className="absolute inset-0 grain-band opacity-20" />
                <div className="relative flex flex-col sm:flex-row items-center justify-between gap-4">
                  <div><h3 className="font-display font-semibold text-xl">{cta.title}</h3><p className="text-slate-300 text-sm mt-1">{cta.text}</p></div>
                  <Link to={cta.to} data-testid="hub-cta-btn" className="shrink-0 inline-flex items-center gap-2 bg-emerald-brand hover:bg-emerald-dark text-white font-semibold rounded-full px-6 py-3 transition-colors">{cta.label} <ArrowRight size={16} /></Link>
                </div>
              </div>
            )}

            {author && (author.bio || author.photo) && (
              <div className="mt-10 flex gap-4 items-start bg-white border border-slate-200 rounded-2xl p-6" data-testid="hub-author-card">
                {author.photo ? <img src={author.photo} alt={author.name} className="h-16 w-16 rounded-full object-cover shrink-0" /> : <span className="h-16 w-16 rounded-full bg-emerald-brand/10 text-emerald-brand flex items-center justify-center font-display font-bold text-xl shrink-0">{author.name[0]}</span>}
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-400">Written by</p>
                  <p className="font-display font-semibold text-navy">{author.name}{author.role ? <span className="text-slate-400 font-normal"> · {author.role}</span> : ""}</p>
                  {author.bio && <p className="mt-1 text-sm text-slate-600 leading-relaxed">{author.bio}</p>}
                  {author.linkedin && <a href={author.linkedin} target="_blank" rel="noreferrer" className="mt-2 inline-block text-sm text-emerald-brand font-medium">LinkedIn →</a>}
                </div>
              </div>
            )}

            <div className="mt-10"><NewsletterBox /></div>

            <Comments slug={slug} />
          </div>
        </section>

        {related.length > 0 && (
          <section className="py-12 bg-slate-50">
            <div className="max-w-5xl mx-auto px-6">
              <h2 className="font-display font-semibold text-navy text-2xl mb-6">You may also like</h2>
              <div className="grid sm:grid-cols-3 gap-5" data-testid="hub-related">
                {related.map((r) => (
                  <Link key={r.id} to={`/hub/${r.slug}`} className="group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all">
                    <div className="aspect-[16/10] bg-navy/5 overflow-hidden">{(r.cover_image || r.video_thumbnail) ? <img src={r.cover_image || r.video_thumbnail} alt={r.title} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-500" /> : null}</div>
                    <div className="p-4"><span className="text-xs text-emerald-brand font-medium">{r.category}</span><h3 className="mt-1 font-display font-semibold text-navy text-sm leading-snug line-clamp-2 group-hover:text-emerald-brand transition-colors">{r.title}</h3></div>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </article>
      <Footer content={content} />
    </div>
  );
}

function Comments({ slug }) {
  const [list, setList] = useState([]);
  const [f, setF] = useState({ name: "", email: "", body: "", website: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const on = (k) => (e) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get(`/hub/${slug}/comments`).then((r) => setList(r.data)).catch(() => {}); }, [slug]);

  const submit = async (e) => {
    e.preventDefault();
    if (!f.name.trim() || !f.email.trim() || !f.body.trim()) { toast.error("Please fill in all fields"); return; }
    setLoading(true);
    try {
      await api.post(`/hub/${slug}/comments`, f);
      setDone(true); setF({ name: "", email: "", body: "", website: "" });
      toast.success("Comment submitted for review");
    } catch (err) { toast.error(apiErr(err.response?.data?.detail) || "Could not submit comment"); }
    finally { setLoading(false); }
  };

  const field = "mt-1.5 focus-visible:ring-emerald-brand";
  return (
    <div className="mt-12" data-testid="hub-comments">
      <h2 className="font-display font-semibold text-navy text-2xl mb-5">Comments {list.length > 0 && <span className="text-slate-400 font-normal text-lg">({list.length})</span>}</h2>
      <div className="space-y-4 mb-8">
        {list.length === 0 ? <p className="text-sm text-slate-400">Be the first to comment.</p> : list.map((c, i) => (
          <div key={i} data-testid={`comment-${i}`} className="bg-white border border-slate-200 rounded-xl p-4">
            <div className="flex items-center gap-3 mb-1.5">
              <span className="h-8 w-8 rounded-full bg-emerald-brand/10 text-emerald-brand flex items-center justify-center text-sm font-semibold">{(c.name || "?")[0].toUpperCase()}</span>
              <span className="font-medium text-navy text-sm">{c.name}</span>
            </div>
            <p className="text-sm text-slate-600 leading-relaxed whitespace-pre-line">{c.body}</p>
          </div>
        ))}
      </div>

      {done ? (
        <div className="bg-emerald-brand/5 border border-emerald-brand/30 rounded-xl p-5 text-sm text-emerald-brand" data-testid="comment-success">Thanks! Your comment is awaiting approval and will appear once reviewed.</div>
      ) : (
        <form onSubmit={submit} data-testid="comment-form" className="bg-white border border-slate-200 rounded-2xl p-6 space-y-4">
          <h3 className="font-display font-semibold text-navy">Leave a comment</h3>
          <div className="grid sm:grid-cols-2 gap-4">
            <Input required data-testid="comment-name" placeholder="Your name" className={field} value={f.name} onChange={on("name")} />
            <Input required type="email" data-testid="comment-email" placeholder="Your email (not published)" className={field} value={f.email} onChange={on("email")} />
          </div>
          <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={on("website")} className="hidden" aria-hidden="true" />
          <Textarea required data-testid="comment-body" rows={4} placeholder="Share your thoughts…" className={field} value={f.body} onChange={on("body")} />
          <Button type="submit" disabled={loading} data-testid="comment-submit" className="bg-emerald-brand hover:bg-emerald-dark rounded-full px-6 font-semibold gap-1.5"><Send size={15} /> {loading ? "Submitting…" : "Post Comment"}</Button>
        </form>
      )}
    </div>
  );
}
